import { DraggableLocation, DropResult } from '@hello-pangea/dnd';
import { useCallback, useState } from 'react';
import { containerOf, StringPlacement } from '@/utils/questions/dnd/resolvePlacement';

type UseClickPlacementArgs = {
  stringPlacement: StringPlacement;
  applyDrop: (result: DropResult) => void;
  disabled?: boolean;
};

const buildDropResult = (
  draggableId: string,
  source: DraggableLocation,
  destination: DraggableLocation,
): DropResult => ({
  draggableId,
  source,
  destination,
  type: 'DEFAULT',
  reason: 'DROP',
  mode: 'SNAP',
  combine: null,
});

export function useClickPlacement({
  stringPlacement,
  applyDrop,
  disabled,
}: UseClickPlacementArgs) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const clearSelection = useCallback(() => setSelectedId(null), []);

  const placeInto = useCallback(
    (destId: string) => {
      if (!selectedId) return;

      const sourceId = containerOf(stringPlacement, selectedId);
      if (!sourceId || sourceId === destId) {
        setSelectedId(null);
        return;
      }

      const sourceIndex = (stringPlacement[sourceId] ?? []).indexOf(selectedId);
      const destIndex = (stringPlacement[destId] ?? []).length;

      applyDrop(
        buildDropResult(
          selectedId,
          { droppableId: sourceId, index: sourceIndex },
          { droppableId: destId, index: destIndex },
        ),
      );
      setSelectedId(null);
    },
    [selectedId, stringPlacement, applyDrop],
  );

  const onChipClick = useCallback(
    (chipId: string) => {
      if (disabled) return;
      if (!selectedId) {
        setSelectedId(chipId);
        return;
      }
      if (selectedId === chipId) {
        setSelectedId(null);
        return;
      }

      const destId = containerOf(stringPlacement, chipId);
      if (destId) placeInto(destId);
      else setSelectedId(null);
    },
    [disabled, selectedId, stringPlacement, placeInto],
  );

  const onContainerClick = useCallback(
    (containerId: string) => {
      if (!disabled && selectedId) placeInto(containerId);
    },
    [disabled, selectedId, placeInto],
  );

  return { selectedId, clearSelection, onChipClick, onContainerClick };
}
